import { Card, SkeletonBar, panelLoadingCss } from '@mnd-frontend/ui';
import styled from 'styled-components';

export const SkeletonWrapper = styled.div<{
  $paddingBottom?: number;
  $animate?: boolean;
  $hideBackground?: boolean;
}>`
  position: relative;
  ${props => props.$animate && panelLoadingCss}
  ${props => props.$paddingBottom && `padding-bottom: ${props.$paddingBottom}rem;`}
  ${props =>
    props.$hideBackground &&
    `&:after {
    background-color: rgba(0, 0, 0, 0);
  }`}
`;

const Flex = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  justify-content: space-between;
`;

export const SkeletonBarChart = () => (
  <SkeletonWrapper $animate={true}>
    <Flex>
      <SkeletonBar borderRadius="sm" height={`${150 / 16}rem`} width="3rem" />
      <SkeletonBar borderRadius="sm" height={`${50 / 16}rem`} width="3rem" />
      <SkeletonBar borderRadius="sm" height={`${350 / 16}rem`} width="3rem" />
      <SkeletonBar borderRadius="sm" height={`${50 / 16}rem`} width="3rem" />
      <SkeletonBar borderRadius="sm" height={`${450 / 16}rem`} width="3rem" />
      <SkeletonBar borderRadius="sm" height={`${250 / 16}rem`} width="3rem" />
      <SkeletonBar borderRadius="sm" height={`${450 / 16}rem`} width="3rem" />
      <SkeletonBar borderRadius="sm" height={`${250 / 16}rem`} width="3rem" />
      <SkeletonBar borderRadius="sm" height={`${350 / 16}rem`} width="3rem" />
      <SkeletonBar borderRadius="sm" height={`${450 / 16}rem`} width="3rem" />
      <SkeletonBar borderRadius="sm" height={`${150 / 16}rem`} width="3rem" />
      <SkeletonBar borderRadius="sm" height={`${50 / 16}rem`} width="3rem" />
    </Flex>
  </SkeletonWrapper>
);

const HitHeader = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-start;
`;

export const SkeletonHit = () => (
  <div>
    <HitHeader>
      <SkeletonBar borderRadius="sm" height="2.5rem" width="2.5rem" />
      <div>
        <SkeletonBar borderRadius="sm" width="18.5rem" height={`${19.5 / 16}rem`} />
        <SkeletonBar borderRadius="sm" width="28.5rem" height={`${26 / 16}rem`} marginTop={0.25} />
      </div>
    </HitHeader>
    <div>
      <SkeletonBar borderRadius="sm" width="36.25rem" height="1.25rem" marginTop={0.5} />
      <SkeletonBar borderRadius="sm" width="26.25rem" height="1.25rem" marginTop={0.5} />
    </div>
    <SkeletonBar
      borderRadius="sm"
      width={`${32.8 / 16}rem`}
      height={`${32.8 / 16}rem`}
      marginTop={0.5}
    />
  </div>
);

const SkeletonMetricsGrid = styled.div`
  margin-top: 1.5rem;
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fill, minmax(25rem, 1fr));
  ${Card} {
    padding: 1.5rem 1.5rem 2.375rem 1.5rem;
  }
`;

const SkeletonMetricsCard = () => (
  <Card>
    <SkeletonBar borderRadius="sm" width="10rem" height="0.5rem" />
    <SkeletonBar borderRadius="sm" width="15rem" height="3.75rem" marginTop={0.75} />
    <SkeletonBar borderRadius="sm" width="16.875rem" height="0.5rem" marginTop={0.75} />
    <SkeletonBar borderRadius="sm" width="13.5rem" height="0.5rem" marginTop={0.25} />
  </Card>
);

export const SkeletonSummaryInsight = () => (
  <SkeletonWrapper $animate={true}>
    <SkeletonBar borderRadius="sm" height="3.75rem" width="100%" />
    <SkeletonBar borderRadius="sm" height="1.5rem" width="70%" marginTop={0.75} />
    <SkeletonBar borderRadius="sm" height="1.5rem" width="100%" marginTop={0.75} />
    <SkeletonBar borderRadius="sm" height="1.5rem" width="80%" marginTop={0.75} />
    <SkeletonBar borderRadius="sm" height="1.5rem" width="75%" marginTop={0.75} />
    <SkeletonBar borderRadius="sm" height="1.5rem" width="85%" marginTop={0.75} />
    <SkeletonBar borderRadius="sm" height="1.5rem" width="50%" marginTop={0.75} />
  </SkeletonWrapper>
);
export const SkeletonInsight = () => (
  <SkeletonWrapper $animate={true} $hideBackground>
    <SkeletonBar borderRadius="sm" height="0.75rem" width="95%" />
    <SkeletonBar borderRadius="sm" height="0.75rem" width="100%" marginTop={0.75} />
    <SkeletonBar borderRadius="sm" height="0.75rem" width="85%" marginTop={0.75} />
    <SkeletonBar borderRadius="sm" height="0.75rem" width="100%" marginTop={0.75} />
  </SkeletonWrapper>
);

export const SkeletonMetrics = () => (
  <SkeletonWrapper $animate={true}>
    <SkeletonMetricsGrid>
      <SkeletonMetricsCard />
      <SkeletonMetricsCard />
      <SkeletonMetricsCard />
      <SkeletonMetricsCard />
      <SkeletonMetricsCard />
    </SkeletonMetricsGrid>
  </SkeletonWrapper>
);

export const SkeletonTable = () => (
  <SkeletonWrapper $animate={true}>
    <SkeletonBar borderRadius="sm" width="100%" height="12rem" />
    <SkeletonBar borderRadius="sm" width="100%" height="12rem" marginTop={1.5} />
  </SkeletonWrapper>
);

const QuotationCardBottom = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  padding: 1rem 1rem 2.25rem 1rem;
  flex-direction: column;
`;
const QuotationCard = styled(Card)`
  border-radius: 1rem;
  overflow: hidden;
`;

export const SkeletonQuotation = () => (
  <QuotationCard>
    <SkeletonWrapper $animate={true}>
      <SkeletonBar borderRadius="unset" width="100%" aspectRatio="16/9" />
      <QuotationCardBottom>
        <SkeletonBar borderRadius="sm" width="8.5rem" height="1rem" />
        <SkeletonBar borderRadius="sm" width="6.25rem" height="0.75rem" />
      </QuotationCardBottom>
    </SkeletonWrapper>
  </QuotationCard>
);
